import { LogUserActivity } from '@/apis/users/requests'
import {
  selectApplicationRequestStatus,
  selectFamilyApplication,
  UpdateIncomeDetails,
} from '@/app-storage/applications/familyApplicationSlice.ts'
import { selectProgramID } from '@/app-storage/program_settings/programSettingsSlice.ts'
import { AppDispatch } from '@/app-storage/store.ts'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { WarningIcon } from '@/app/common/icons/support'
import { Button } from '@/common/components/Button'
import { AppRoutePaths } from '@/routing/route_paths'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { LOG_TYPES } from '../../constants'
import useFamilyApplicationRouting from '../../routing/useFamilyApplicationRouting'

type navigateProps = {
  onCancel: () => void
}

export const SkipVerification = ({ onCancel }: navigateProps) => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const FamilyApplication = useSelector(selectFamilyApplication)
  const ProgramId = useSelector(selectProgramID)
  const StoreDispatch = useDispatch<AppDispatch>()
  const { onModuleEnd } = useFamilyApplicationRouting(FamilyApplication)
  const { fireAlert } = useAppAlert()
  const ApplicationStatus = useSelector(selectApplicationRequestStatus)

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Skip Income verification page',
      })
      hasLoggedPageView.current = true;
    }
  }, []) 

  const handleDontVerifyIncome = async () => {
    const ActionResult = await StoreDispatch(
      UpdateIncomeDetails({
        skip_income_verification: true,
        program_id: ProgramId,
      })
    )

    if (UpdateIncomeDetails.fulfilled.match(ActionResult)) {
      navigate(onModuleEnd(AppRoutePaths.family_application.review_application))
    } else {
      await fireAlert({
        variant: 'error',
        message: ActionResult.payload?.message,
      })
    }
  }

  return (
    <section aria-labelledby="skip-verification-title text-center">

      <div className="flex justify-center items-center gap-2 mb-4 mt-2">
        <WarningIcon className="text-4xl text-amber-500" />
        <h2
          id="skip-verification-title"
          className="text-xl font-medium text-gray-900"
        >
          {translate('skip_income_verification')}
        </h2>
      </div>

      <p className="text-gray-700 flex">
        {translate('you_may_opt_out_income_verification')}
      </p>
      <div className="text-center h-full flex">
        <div className="flex w-full flex-col space-y-4 sm:justify-end sm:mt-10 !mt-10">
          <Button
            onClick={() => {
              onCancel()
            }}
            className="h-10 bg-gray-200 text-gray-900 hover:bg-gray-100"
            aria-label={translate('cancel')}
          >
            {translate('i_dont_agree')}
          </Button>
          <Button
            onClick={handleDontVerifyIncome}
            className="h-10 bg-danger hover:bg-danger hover:opacity-80"
            disabled={ApplicationStatus === 'in_flight'}
            aria-label={translate('dont_verify_my_income')}
          >
            {ApplicationStatus === 'in_flight'
              ? `${translate('submitting')}...`
              : translate('agree')}
          </Button>
        </div>
      </div>
    </section>
  )
}
